import React, {useCallback, useState, useEffect} from "react";
import { BookOpen } from "lucide-react";
import CTAButton from "@/components/CTAButton";
import SectionHeader from "./SectionHeader";
import ScriptCarousel from "./ScriptCarousel";
import { Script } from "./ScriptCard";
import {Supply} from "@/types/game.ts";
import scriptService from "@/services/scriptService.ts";
import mcApi from "../../services/api/mc.ts";
import { getScriptCover } from "@/utils/imageUtils";
import { getScriptTypeName } from "@/config/scriptConfig";

// Export script data so it can be reused in FeaturedGamesSection
export const scriptData: Script[] = [
  {
    id: "script-1",
    title: "Spring in Pripyat",
    type: "Horror",
    secondaryType: "Deduction",
    image: "https://static.anzirtech.com/fabletop/202406593888cc-2809-4baf-a084-d70ea6d47449.png",
    author: "AnzirAct Team",
    description: "The Fowler family were just trying to escape the storm. Locked in their cellar, they thought they were safe... until they awoke to a strange man in their cellar with them. He knows the way out and how to navigate the now ripped apart Overtown, but is trusting him a good idea? This man, Sebastian Bradshaw, seems to know a little too much about the family and their secrets. Navigate the city and get away from Sebastian without having your secrets outed by him, and be careful who you can trust in this twisted horror game.",
    players: "6-8 players",
    contentWarning: "Drug References, Violence, Political Themes",
    language: "English",
    playTime: "4 Hours",
    authorNotes: "Players should be familiar with movies like The Purge series and Parasite for this game to work out. I'd suggest a movie night before playing, but that's just me. Thanks for trying this one out! Whoever choses the Sebastian Bradshaw card is the GM of this game.",
    link: "/script/66c2f3cb7922c97c225b1047/666f97408f91681c30fac571"
  },
  {
    id: "script-3",
    title: "The Throne",
    type: "Rule-based",
    image: "/lovable-uploads/11ec49be-dfde-4eea-8f92-f3e5a3624e6d.webp",
    author: "AnzirAct Team",
    description: "The Ends of Deities A snowy night in Asgard, bringing the Vikings a freezing arctic wind.. The Frozen Throne is unoccupied, waiting for his master. One puts out the torch illuminating the night, and brings the moon back shining in the sky. But one doesn't know; true power belongs to the ultimate Deity. You are a god; how can you become a deity? Fight for your future with the weapons in your hands.",
    players: "4M3F players",
    language: "English",
    playTime: "4 Hours",
    wordCount: "67886 Words",
    link: "/script/677cb5a585c9f156cab12454/677cb5a5af2afb0694741f98"
  },
  {
    id: "script-4",
    title: "Kage Rock",
    type: "Rule-based",
    secondaryType: "Horror",
    image: "https://static.anzirtech.com/fabletop/202403/6407b573-e837-45d4-94ce-b6dfe9dbe111.png",
    author: "Wolf Pack",
    description: "In the town of Grysdale you are part of a group of wolves who hide among humans during the day. At night you hide in the woods watching over the town for any trouble. One damp foggy night the group sees a white flash of fur run into the town... The leader of the group has a flashback and tells the group of a White Wolf that once roamed the town long ago... Is this the same White Wolf or is it a son or daughter of the White Wolf Walking? Do you dare to find out what lurks in the town?",
    players: "3M2F players",
    contentWarning: "Violence, Violence will happen when wolves interact with one another with weapons and fighting.",
    language: "English",
    playTime: "0 Hours",
    authorNotes: "There are two factions. Faction 1 is the Wolves who have grown up in town and Faction 2 is the White Wolf who walks. Violence will occur in some parts of the game, since the White Wolf Walking will not listen or go down easy. Do you dare to take on the White Wolf that walks the town?",
    link: "/script/66c2f3ce7922c97c225b10f1/65e03ff74242130d5c9ce5d7"
  },
  {
    id: "script-5",
    title: "The City that Never Sleeps",
    type: "Horror",
    secondaryType: "Deduction",
    image: "https://static.anzirtech.com/fabletop/202405c42a03e5-023b-4d52-aa6e-b28640298c31.png",
    author: "MyKey_Wazowsky",
    description: "The year is 2684. Humanity has thrived in the aftermath of a disastrous apocalypse. The People's Union is now the governing force for the whole world, made up of leaders from around the globe. Creatures once thought to be made of myth have come out of the shadows and integrated with human life. This year, a team has been assembled for the first long distance space exploration mission to an unknown planet we've been receiving signals from for over a thousand years.",
    players: "5-8 players",
    link: "/script/66c2f3d37922c97c225b119d/6655a4e08f91681c30fac3fa"
  },

];

const FindGamesSection = () => {
  const [scripts, setScripts] = useState<Script[]>([]);
  const [loading, setLoading] = useState(true);
  // 获取Online剧本列表
  const fetchOnlineScript = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        "match": {
          "op": "AND",
          "statements": [
            {
              "fieldName": "phaseType",
              "symbol": "IN",
              "value": [
                "SALE",
                "CLOSED_TEST"
              ]
            },
            {
              "fieldName": "type",
              "symbol": "EQUAL",
              "value": "SCRIPT"
            },
            {
              "fieldName": "saleState",
              "symbol": "EQUAL",
              "value": "ON_SALE"
            }
          ]
        },
        "sort": [
          {
            "fieldName": "weight",
            "symbol": "DESC"
          },
          {
            "fieldName": "updateTime",
            "symbol": "DESC"
          }
        ],
        "page": {
          "page": 0,
          "pageSize": 10
        }
      };
      const response = await mcApi.supply.query(params);
      if (response.code === 1000 && response.data) {
        // Convert Supply data to Script type
        const supplies = Array.isArray(response.data) ? response.data : [response.data];
        const convertedScripts: Script[] = supplies.map((supply: any) => {
          // Get script type from scriptType array
          const scriptType = supply.content.scriptType?.[0] || 4; // Default to Rule-based (4) if not specified
          const typeName = getScriptTypeName(scriptType) as "Deduction" | "Horror" | "Fun" | "Rule-based" | "Emotional";

          // 处理游戏时长
          const duration = supply.content.duration || 0;
          let playTimeText = undefined;
          if (duration > 0) {
            playTimeText = duration === 1 ? "1 Hour" : `${duration} Hours`;
          }

          return {
            id: supply.id,
            title: supply.name,
            type: typeName,
            secondaryType: supply.content.scriptType?.[1] ? getScriptTypeName(supply.content.scriptType[1]) as "Deduction" | "Horror" | "Fun" | "Rule-based" | "Emotional" : undefined,
            tertiaryType: supply.content.scriptType?.[2] ? getScriptTypeName(supply.content.scriptType[2]) as "Deduction" | "Horror" | "Fun" | "Rule-based" | "Emotional" : undefined,
            image: getScriptCover(supply.content.artwork_cover || supply.content.cover),
            author: supply.content.authorName || "Unknown Author",
            description: supply.content.description,
            players: `${supply.content.roleCount} players`,
            contentWarning: supply.content.customContentWarning || undefined,
            language: supply.content.language.map(lang => lang.toString()).join(", "),
            playTime: playTimeText,
            link: `/script/${supply.content.stableId}/${supply.id}`
          };
        });
        setScripts(convertedScripts);
      }
    } catch (error) {
      console.error('Failed to fetch online scripts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOnlineScript();
  }, [fetchOnlineScript]);

  return (
    <div className="h-full flex flex-col">
      <SectionHeader
        preTitle="STEP 1"
        title="Find Your Perfect MysteryAct"
        description="Browse our curated selection of thrilling games to start your adventure"
        showStepPrefix={true}
      />

      {loading ? (
        <div className="flex-grow flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-anzir"></div>
        </div>
      ) : (
        <ScriptCarousel scripts={scripts.length > 0 ? scripts : scriptData} />
      )}

      <div className="text-center mt-auto mb-8">
        <p className="text-muted-foreground mb-6 text-lg">
          Discover many more offline MysteryAct games in our complete library
        </p>
        <CTAButton to="/library" icon={<BookOpen className="h-5 w-5" />} className="bg-anzir hover:bg-anzir/90">
          Explore Full Library
        </CTAButton>
      </div>
    </div>
  );
};

export default FindGamesSection;
